/**
 * RecentlyRead — a clean cover grid. Compact covers, subtle metadata
 * (rating + date finished) underneath; the covers stay the visual weight.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SectionHeader, Stars } from '@/components/site/primitives';
import { fadeUp, stagger, viewport } from '@/lib/motion';
import { bookHref } from '@/lib/bookModel';
import { formatDateFinished } from './utils/profileHelpers';
import BookCover from '@/components/books/BookCoverArt';
import EmptyState from './EmptyState';
import { PROFILE } from '@/constants/testIds';

export default function RecentlyRead({ books }) {
  return (
    <section className="u-container mt-16 md:mt-24" data-testid={PROFILE.recentlyRead}>
      <SectionHeader eyebrow="The shelf so far" title="Recently Read" />

      {books.length === 0 ? (
        <EmptyState type="recentlyRead" to="/app/explore" compact className="mt-2" />
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-8 grid grid-cols-3 gap-x-4 gap-y-7 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6"
        >
          {books.map((book) => (
            <motion.div key={book.id} variants={fadeUp}>
              <Link to={bookHref(book.id)} className="group block">
                <BookCover book={book} className="w-full transition-transform duration-300 ease-soft group-hover:-translate-y-1 group-hover:shadow-lift" />
              </Link>
              <div className="mt-2">
                <h3 className="line-clamp-1 font-display text-[13px] font-semibold text-ink">{book.title}</h3>
                <p className="truncate text-[11px] text-subtle">{book.author}</p>
                <div className="mt-1 flex items-center justify-between">
                  {book.rating ? <Stars value={Math.round(book.rating)} size={10} /> : <span />}
                  {book.dateFinished && <span className="text-[10px] text-subtle/80">{formatDateFinished(book.dateFinished)}</span>}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </section>
  );
}
